// Sidebar + header shell of every admin page.
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import { api } from '../services/api';
import { Button } from './ui';

const NAV: { to: string; key: string; end?: boolean }[] = [
  { to: '/', key: 'nav.dashboard', end: true },
  { to: '/users', key: 'nav.users' },
  { to: '/organizations', key: 'nav.organizations' },
  { to: '/contact-requests', key: 'nav.contact' },
  { to: '/waitlist', key: 'nav.waitlist' },
  { to: '/insights', key: 'nav.insights' },
  { to: '/conversations', key: 'nav.conversations' },
  { to: '/sandboxes', key: 'nav.sandboxes' },
  { to: '/issues', key: 'nav.issues' },
  { to: '/offers', key: 'nav.offers' },
  { to: '/packs', key: 'nav.packs' },
  { to: '/prices', key: 'nav.prices' },
  { to: '/credits', key: 'nav.credits' },
  { to: '/orders', key: 'nav.orders' },
  { to: '/vat', key: 'nav.vat' },
  { to: '/invoices', key: 'nav.invoices' },
  { to: '/monthly', key: 'nav.monthly' },
  { to: '/mail', key: 'nav.mail' },
  { to: '/audit', key: 'nav.audit' },
];

/** The Sculpt Agent mark, same as the add-in (peerception-agent-v2 Header.tsx) and sculpt-website: green tile, 3×3 grid
 *  with its centre cell filled white, four-point star. */
export function SculptMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="100 150 780 780" role="img" aria-label="Sculpt">
      <g transform="translate(119.47,221.87)">
        <rect width="682.67" height="682.67" rx="136.53" fill="#107C41" />
        <rect x="119.47" y="119.47" width="443.73" height="443.73" rx="51.2" fill="none" stroke="#FFFFFF" strokeWidth="25.6" />
        <g stroke="#FFFFFF" strokeWidth="25.6">
          <line x1="119.47" y1="264.53" x2="563.2" y2="264.53" />
          <line x1="119.47" y1="418.13" x2="563.2" y2="418.13" />
          <line x1="264.53" y1="119.47" x2="264.53" y2="563.2" />
          <line x1="418.13" y1="119.47" x2="418.13" y2="563.2" />
        </g>
        <rect x="264.53" y="264.53" width="153.6" height="153.6" fill="#FFFFFF" />
        <circle cx="631.47" cy="51.2" r="128" fill="#FFFFFF" />
        <path d="M631.47 -51.2 L657.07 25.6 L733.87 51.2 L657.07 76.8 L631.47 153.6 L605.87 76.8 L529.07 51.2 L605.87 25.6 Z" fill="#107C41" />
      </g>
    </svg>
  );
}

/** A count of things waiting for staff beside a nav entry, read again on each navigation; 0 when it cannot be read. */
function useWaitingCount(load: () => Promise<number>): number {
  const { pathname } = useLocation();
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    load().then(
      (n) => live && setCount(n),
      () => live && setCount(0),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return count;
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const { me, signOut } = useSession();
  // VAT numbers waiting for a decision, and contact requests nobody has picked up yet.
  const waiting: Record<string, number> = {
    '/vat': useWaitingCount(() => api.vatReviews().then((rows) => rows.length)),
    '/contact-requests': useWaitingCount(() => api.contactRequests({ status: 'new', page_size: 1 }).then((page) => page.total)),
  };
  return (
    <div className="flex h-full min-h-screen">
      <aside className="flex w-52 shrink-0 flex-col border-r border-line-soft bg-white">
        <div className="flex h-12 items-center gap-2 border-b border-line-soft px-4">
          <SculptMark />
          <div className="leading-tight">
            <div className="text-[13px] font-medium text-ink">Sculpt</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-faint">{t('app.backoffice')}</div>
          </div>
        </div>
        <nav className="flex flex-col gap-0.5 p-2">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `rounded-[5px] px-3 py-1.5 text-[13px] ${isActive ? 'bg-accent-bg font-medium text-accent-dark' : 'text-muted hover:bg-hover-soft hover:text-ink'}`}>
              {t(item.key)}
              {(waiting[item.to] ?? 0) > 0 && (
                <span className="ml-1.5 rounded-full bg-warn-bg px-1.5 font-mono text-[10px] text-warn" data-testid={`nav-${item.to.slice(1)}-count`}>
                  {waiting[item.to]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center justify-end gap-3 border-b border-line-soft bg-white px-5">
          <span className="font-mono text-[11px] text-faint" data-testid="header-email">
            {me?.email}
          </span>
          <button type="button" onClick={() => void i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')} className="h-7 rounded-[5px] px-2 font-mono text-[11px] text-muted hover:bg-hover-soft" title={t('header.language')}>
            {i18n.language === 'fr' ? 'EN' : 'FR'}
          </button>
          <Button variant="ghost" onClick={() => void signOut()}>
            {t('header.signOut')}
          </Button>
        </header>
        <main className="min-w-0 flex-1 overflow-auto px-6 py-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
